// This was generated during the migration to @kiwicom/eslint-config
// PLEASE REMOVE THE GLOBAL ESLINT DISABLE BELOW
// AND FIX ERRORS ACCORDINGLY.
/* eslint-disable babel/camelcase */
import type { Dispatch } from "redux"
import { currencySelector } from "@kiwicom/frontend-common/services/currencies/selectors"
import Price from "@kiwicom/frontend-common/records/Price"
import { MODALS } from "@kiwicom/frontend-common/consts/modals"
import { bidSelector } from "@kiwicom/frontend-common/services/booking/booking"

import { SERVICES, Category } from "../../../../../../../../consts/additionalServices"
import { processFreePayment } from "../helpers/helpers"
import type { GetState } from "../../../../services/types"
import mmbTrack from "../../../../../../../../services/tracking/mmbTrack"
import {
  ErrorOccurredAction,
  GetDataAction,
  MMB2Module,
  ModalOpenAction,
  PaymentModule,
  PostFormAction,
} from "../../../../../../../../services/tracking/constants/trackingEvents"
import { CONFIRM_SUCCESS_FREE_EXTRA } from "../../../../services/changeFlights/consts"
import translate from "@kiwicom/frontend-app/scripts/tools/translate"
import { EDITING_STEPS } from "../../../BookingPassengerEdit/components/constants"
import { setStep } from "../../../BookingPassengerEdit/services/PassengerEditDuck"
import * as api from "../api"
import { showFeatureInMMB2 } from "../../../../../../../../services/mmb2/mmb2RedirectAction"
import { REDIRECT_TO_SHOPPING_BASKET_FAILURE } from "../../../../../../../../services/mmb2/types"
import type { ShoppingBasketOutbound } from "../api/types"
import type { BookingId, Currency } from "../../../../../../../../types/primitiveTypes"
import { SHOPPING_BASKET_UPDATING_END, SHOPPING_BASKET_UPDATING_START } from "../constants/conts"
import { openModalAction } from "../../../../services/bookingActions"
import { mappedShoppingBasketResponse } from "../api/types"

type CreateAndUpdateArgs = {
  bid: BookingId
  category: Category
  currency: Currency
  shoppingBasketPayload: ShoppingBasketOutbound
}

/**
 * Creates a new shopping basket and updates it with given items
 *
 * We're throwing errors from API calls so the tracking can be done in the catch block, locally
 *
 * @returns {Promise<void>}
 */
export const createAndUpdateShoppingBasket = async ({
  bid,
  category,
  currency,
  shoppingBasketPayload,
}: CreateAndUpdateArgs): Promise<mappedShoppingBasketResponse> => {
  mmbTrack(
    {
      module: PaymentModule,
      action: GetDataAction,
      detail: "load shopping basket",
    },
    { type: "load shopping basket", currency },
  )
  const { basket_id } = await api.fetchShoppingBasket(bid, currency).catch((e) => {
    mmbTrack(
      {
        module: PaymentModule,
        action: ErrorOccurredAction,
        detail: "Failed to create shopping basket",
      },
      { currency, category },
    )
    // throwing error for the parent function (handlePaymentRedirect), so we can open 'Payment failed' modal there
    throw e
  })

  mmbTrack(
    {
      module: PaymentModule,
      action: PostFormAction,
      detail: "update basket",
    },
    {
      updatedBasket: shoppingBasketPayload,
      category,
      basketId: basket_id,
      type: "update basket",
    },
  )

  const {
    common: { basket },
  } = await api.updateShoppingBasket(bid, basket_id, shoppingBasketPayload).catch((e) => {
    mmbTrack(
      {
        module: PaymentModule,
        action: ErrorOccurredAction,
        detail: "Failed to add to shopping basket",
      },
      { basketId: basket_id, currency, category },
    )
    // throwing error for the parent function (handlePaymentRedirect), so we can open 'Payment failed' modal there
    throw e
  })

  return { basket_id, basket }
}

/**
 * returns action to dispatch in order to open correct thank you modal after processing free payment
 * @param category
 * @returns {{payload: {stage: *, step: *}, type: string}|null|{type: string}}
 */
const getActionForFreeThankYouModalToDispatch = (category: Category) => {
  switch (category) {
    case SERVICES.CHANGE_FLIGHTS:
      return { type: CONFIRM_SUCCESS_FREE_EXTRA }
    case SERVICES.PASSENGER_EDIT:
      return setStep({ step: EDITING_STEPS.THANK_YOU })
    case SERVICES.ASSISTED_REFUND:
      return null
    default:
      mmbTrack(
        {
          module: PaymentModule,
          action: ModalOpenAction,
        },
        {
          detail: "Opening modal for free payment",
          category,
        },
      )
      return openModalAction(MODALS.ORDER_SUCCESS)
  }
}

export type HandlePaymentRedirect = (
  arg1: ShoppingBasketOutbound,
  arg2: Category,
  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => (arg1: Dispatch<any>, arg2: GetState) => Promise<any>

/**
 * Add items to be paid to the shopping basket before redirecting to MMB2 Payment page
 *
 * OR processes free payment and shows correct thank you modal. In case of free items, we're relying on BBE business logic.
 * Client adds only items and it's prices to the shopping basket without a regard to handling free.
 * Client is relying on BBE to correctly update total price and compute the correct handling fee.
 * Based on this updated to total price, we're opening thank you modals for free services.
 *
 * @param shoppingBasketPayload - the prices are already converted from EUR to the currency the user has selected in the UI
 * @param category - client side category (used for tracking)
 *
 * @returns {Promise<boolean>}
 */
export const handlePaymentRedirectAction = (
  shoppingBasketPayload: ShoppingBasketOutbound,
  category: Category,
  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => async (dispatch: Dispatch<any>, getState: GetState): Promise<boolean> => {
  dispatch({ type: SHOPPING_BASKET_UPDATING_START })
  const state = getState()
  const bid = bidSelector(state)
  const currency = currencySelector(state)

  try {
    const { basket_id, basket } = await createAndUpdateShoppingBasket({
      bid,
      currency,
      shoppingBasketPayload,
      category,
    })

    const totalPrice = Price.fromServer(basket.price_breakdown.total_price)
    if (totalPrice.amount === 0) {
      await processFreePayment({ bid, basket_id, currency, category })

      const actionToDispatch = getActionForFreeThankYouModalToDispatch(category)

      if (actionToDispatch) {
        dispatch(actionToDispatch)
      }

      // return here, so we don't go to MMB 2 payment page
      dispatch({ type: SHOPPING_BASKET_UPDATING_END })
      return true
    }

    showFeatureInMMB2({
      nextModal: MODALS.PAYMENT,
      state,
      dynamicRouteValues: {
        basketId: basket_id,
        currency: currencySelector(state),
      },
    })

    return true
    // Fix this the next time the file is edited. (Flow to TS)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    mmbTrack(
      {
        module: MMB2Module,
        action: ErrorOccurredAction,
        detail: "redirect to mmb2 payment error",
      },
      { type: "redirect to mmb2 payment error", error },
    )
    dispatch({
      type: REDIRECT_TO_SHOPPING_BASKET_FAILURE,
      error: translate("mmb.payment.error.description.general"),
    })

    return false
  }
}
